import React from 'react';
import { Alert, Button, Empty, Typography } from 'antd';
import PageShell from '../components/page/PageShell';
import PageHeader from '../components/page/PageHeader';
import PageState from '../components/page/PageState';
import PageTabs from '../components/page/PageTabs';
import { useApp } from '../context/AppContext';
import { useApiRequest } from '../hooks/useApiRequest';
import { useResource } from '../hooks/useResource';
import { useTrainingAvailability } from '../hooks/useTrainingAvailability';
import CourseManager from '../components/training/CourseManager';
import EnrollmentHistory from '../components/training/EnrollmentHistory';
import LmsIntegrationsPanel from '../components/training/LmsIntegrationsPanel';
import LearnerTraining from '../components/training/LearnerTraining';
import { canAuthorTraining, canConfigureLms, isTrainingLearner } from '../utils/trainingModel';

const { Title, Text } = Typography;

/**
 * Training (Sheet-2 row 9) — ISO/IEC 27002:2022 A.6.3 Awareness, education and training;
 * .claude/design/training-ai-video-realtime-translation.md.
 *
 * One project-scoped destination, three persona shapes (each gated here AND by the API):
 *   Advisor / Lead Advisor : Courses (author, AI-draft, translate, review, publish*, enrol),
 *                            Training history, LMS integration*       (* Lead Advisor only)
 *   Company SPOC / Delegate: My training (take the course, quiz, certificate)
 *   anyone else            : no training surface
 *
 * Not built, deliberately absent rather than shown disabled: realtime voice-to-text /
 * translation / audio dub and subtitles (design doc "Deferred to v2" — needs speech and
 * translation provider credentials and a media gateway this repo does not have).
 */
const TrainingPage = () => {
    const { user, activeProject } = useApp();
    const { request } = useApiRequest();
    const role = user?.role;
    const projectId = activeProject?.id;
    const authoring = canAuthorTraining(role);
    const learner = isTrainingLearner(role);
    const availability = useTrainingAvailability(projectId, !!projectId && (authoring || learner));
    const closed = availability.available === false;
    const waiting = availability.loading;

    // Authoring personas share one course list (Courses tab + history's course-title join) and
    // the member roster (enrol picker + learner names). The API scopes both to the caller.
    const courses = useResource(
        async () => (await request('GET', `/projects/${projectId}/training/courses`)).data.courses || [],
        [projectId, request],
        { enabled: authoring && !!projectId && !closed && !waiting },
    );
    const members = useResource(
        async () => (await request('GET', `/projects/${projectId}/members`)).data.members || [],
        [projectId, request],
        { enabled: authoring && !!projectId && !closed && !waiting },
    );

    if (!activeProject) {
        return <PageState status="empty" message="Open a project to see its training." />;
    }
    if (!authoring && !learner) {
        return <PageState status="empty" message="Training is not available for your role on this project." />;
    }

    if (waiting && (authoring || learner)) {
        return <PageState status="loading" />;
    }
    if (closed && learner) {
        return (
            <PageShell>
                <Empty
                    description={
                        <span data-testid="training-stage-closed">
                            Training opens after Risk Analysis (Stage 6) is approved. Every Statement of Applicability control must also be marked Applicable or Not applicable. Until then you cannot start or continue training; your completed training and certificates below stay available.
                        </span>
                    }
                />
                <LearnerTraining projectId={projectId} readOnly />
            </PageShell>
        );
    }
    if (closed) {
        return (
            <Empty
                description={
                    <span data-testid="training-stage-closed">
                        Training opens after Risk Analysis (Stage 6) is approved. Once the risk register is finalised and the treatment plan is published, and every Statement of Applicability control is marked Applicable or Not applicable, it becomes available here.
                    </span>
                }
            />
        );
    }

    const header = (
        <PageHeader
            title="Training"
            subtitle={`${activeProject.companyName || activeProject.name} - security awareness and competence (ISO/IEC 27002:2022 A.6.3)`}
        />
    );

    if (learner) {
        return (
            <PageShell>
                {header}
                <LearnerTraining projectId={projectId} />
            </PageShell>
        );
    }

    const items = [
        {
            key: 'courses',
            label: 'Courses',
            children: (
                <CourseManager
                    projectId={projectId}
                    role={role}
                    members={members.data || []}
                    courses={courses}
                    onReload={courses.reload}
                />
            ),
        },
        {
            key: 'history',
            label: 'Training history',
            children: <EnrollmentHistory projectId={projectId} role={role} courses={courses.data || []} members={members.data || []} />,
        },
    ];
    if (canConfigureLms(role)) {
        items.push({ key: 'lms', label: 'LMS integration', children: <LmsIntegrationsPanel projectId={projectId} /> });
    }

    return (
        <PageShell>
            {header}
            {members.status === 'error' && (
                <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 12 }}
                    message="Could not load the project team"
                    description="You can still author courses, but learner names and the enrolment picker need the team list."
                    action={<Button size="small" onClick={members.reload}>Retry</Button>}
                />
            )}
            <PageTabs defaultActiveKey="courses" items={items} />
        </PageShell>
    );
};

export default TrainingPage;
